import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AdminRole } from '../services/auth';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  /** Only matches the exact path, for the dashboard index route. */
  end?: boolean;
  superAdminOnly?: boolean;
  /** When set, only these roles see the item. */
  roles?: AdminRole[];
}

interface NavGroup {
  heading: string;
  items: NavItem[];
}

/** Content tools are admin-only, matching the server-side route guards. */
const CONTENT_ROLES: AdminRole[] = ['admin', 'superadmin'];

const icon = (path: string) => (
  <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d={path} />
  </svg>
);

const NAV: NavItem[] = [
  {
    to: '/admin',
    label: 'Dashboard',
    end: true,
    roles: CONTENT_ROLES,
    icon: icon('M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'),
  },
  {
    to: '/admin/portfolio',
    label: 'Portfolio',
    roles: CONTENT_ROLES,
    icon: icon('M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'),
  },
  {
    to: '/admin/blog',
    label: 'Blog',
    roles: CONTENT_ROLES,
    icon: icon('M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z'),
  },
  {
    to: '/admin/email',
    label: 'Send email',
    end: true,
    icon: icon('M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'),
  },
  {
    to: '/admin/email/bulk',
    label: 'Bulk email',
    icon: icon('M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'),
  },
  {
    to: '/admin/admins',
    label: 'Admins',
    superAdminOnly: true,
    icon: icon('M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z'),
  },
  {
    to: '/admin/account',
    label: 'Account',
    icon: icon('M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z'),
  },
];

/**
 * The partner acquisition and referral module. Grouped under its own heading
 * because it is a distinct job from managing the site's content — a marketer
 * lives entirely in here.
 */
const PARTNER_NETWORK: NavGroup = {
  heading: 'Partner Network',
  items: [
    {
      to: '/admin/partners/prospects',
      label: 'Partner prospects',
      icon: icon('M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'),
    },
    {
      to: '/admin/partners/partners',
      label: 'Active partners',
      icon: icon('M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z'),
    },
    {
      to: '/admin/partners/referrals',
      label: 'Referrals',
      icon: icon('M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'),
    },
    {
      to: '/admin/partners/commissions',
      label: 'Commissions',
      roles: CONTENT_ROLES,
      icon: icon('M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'),
    },
  ],
};

/**
 * Shell for every signed-in admin page: a fixed sidebar on desktop, a slide-in
 * drawer on mobile. Replaces the marketing header/footer, which does not belong
 * over a dashboard.
 */
const AdminLayout: React.FC = () => {
  const { admin, signOut } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Navigating on mobile should close the drawer behind you.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // A drawer over the page shouldn't leave the page scrolling underneath it.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  const visible = (item: NavItem) => {
    if (item.superAdminOnly && admin?.role !== 'superadmin') return false;
    if (item.roles && (!admin || !item.roles.includes(admin.role))) return false;
    return true;
  };

  const items = NAV.filter(visible);
  const partnerItems = PARTNER_NETWORK.items.filter(visible);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
      isActive
        ? 'bg-primary-500/10 text-primary-400 border border-primary-500/30'
        : 'text-muted hover:text-white hover:bg-surface-hover border border-transparent'
    }`;

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-surface-border">
        <div className="flex items-center gap-3">
          <img src="/nexalogo.png" alt="Nexasolutra" className="w-8 h-8 object-contain" />
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold leading-tight">Nexasolutra</p>
            <p className="text-[10px] uppercase tracking-widest text-muted">Admin</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {items.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
            {item.icon}
            <span className="truncate">{item.label}</span>
          </NavLink>
        ))}

        {partnerItems.length > 0 && (
          <>
            <p className="px-3 pt-5 pb-2 text-[10px] uppercase tracking-widest text-muted/70">
              {PARTNER_NETWORK.heading}
            </p>
            {partnerItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                {item.icon}
                <span className="truncate">{item.label}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="px-3 py-4 border-t border-surface-border">
        <div className="px-3 pb-3">
          <p className="text-sm text-white truncate">{admin?.name || admin?.email}</p>
          <p className="text-xs text-muted truncate">{admin?.email}</p>
          {admin && admin.role !== 'admin' && (
            <span className="inline-block mt-2 px-2 py-0.5 rounded text-[10px] border border-primary-500/40 bg-primary-500/10 text-primary-400">
              {admin.role === 'superadmin' ? 'super admin' : 'business admin'}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={signOut}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted hover:text-white hover:bg-surface-hover transition-colors"
        >
          {icon('M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1')}
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-surface">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-surface-card border-r border-surface-border z-40">
        {sidebarContent}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <aside className="relative w-64 max-w-[80%] bg-surface-card border-r border-surface-border animate-slide-right">
            {sidebarContent}
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-4 h-14 bg-surface-card/95 backdrop-blur border-b border-surface-border">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            className="p-2 -ml-2 text-muted hover:text-white"
          >
            {icon('M4 6h16M4 12h16M4 18h16')}
          </button>
          <img src="/nexalogo.png" alt="" className="w-6 h-6 object-contain" />
          <span className="text-white text-sm font-medium">Admin</span>
        </header>

        <main className="min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
